import React, { useEffect, useState, useCallback } from "react";
import { Outlet, NavLink, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    UserList, ChartBar, SignOut, Headset, ClipboardText, Package, Briefcase, Lifebuoy,
    PencilSimple, ArrowUUpLeft, ArrowsClockwise, ShieldWarning, PaperPlaneRight,
    Stethoscope, ShieldCheck, CheckCircle, Truck, Heart, Tray, ChatCircleDots, User as UserIcon,
    Handshake, ChartLineUp, Bell, SidebarSimple,
} from "@phosphor-icons/react";
import InvisiqueLogo from "@/components/site/Logo";
import ErrorBoundary from "@/components/site/ErrorBoundary";
import ProductionNotificationBell from "@/components/production/ProductionNotificationBell";
import PlannerNotificationBell from "@/components/planner/PlannerNotificationBell";

/* iter101.58 — Session-scoped collapse memory (matches AdminLayout /
   PortalLayout convention). Kept in sessionStorage so the toggle
   snaps back to the universal "expanded" default when the user
   returns after a browser restart. */
const COE_COLLAPSED_KEY = "invq_coe_sidebar_collapsed";

/**
 * Sidebar nav.
 *
 * Supports an optional `subnav` array per item — when present, renders a sub-list
 * of bucket links. Each sub-link sets a `?bucket=<id>` query param that the
 * destination page reads to filter its list. Bookmarkable + shareable.
 */
const NAV_LEADS = [
    { to: "/coe", icon: ChartBar, label: "Dashboard", end: true },
    // iter98.99ea — Shared inbox: PES picks up unassigned leads
    // (web / b2b / applications) without waiting on admin. Sits above
    // "My Queue" because the proactive lane should feel like step 1.
    { to: "/coe/inbox", icon: Tray, label: "Shared Inbox" },
    // iter98.99ec — Renamed "My Leads" → "My Queue". This page now
    // shows BOTH admin-assigned and PES self-claimed leads; the page
    // is where the specialist actions everything they own.
    { to: "/coe/leads", icon: UserList, label: "My Queue" },
    { to: "/coe/compliance", icon: Heart, label: "Compliance" },
    // iter98.99dx — Phase B: dedicated coach-centric escalations inbox.
    { to: "/coe/escalations", icon: ShieldWarning, label: "Escalations" },
    // iter101.39 — Parity with Planner / Production / Technician sidebars.
    // Uses the same generic StaffProfile page so nothing role-specific
    // needs to be built.
    { to: "/coe/profile", icon: UserIcon, label: "My Profile" },
];

/* Planner sidebar intentionally has no bucket subnav anymore — the My Queue
   page renders its own filter chip bar. Other staff types may still use a
   subnav (e.g. QA_SUBNAV below). */

const QA_SUBNAV = [
    { id: "pending",       icon: ShieldCheck,     label: "Pending QA review" },
    { id: "with_planner",  icon: ArrowUUpLeft,    label: "Back with planner" },
    { id: "with_doctor",   icon: Stethoscope,     label: "With doctor" },
    { id: "plan_revision", icon: ArrowsClockwise, label: "Plan revision" },
    { id: "adr",           icon: ShieldWarning,   label: "ADR — hold" },
    { id: "refinement",    icon: ArrowsClockwise, label: "In refinement" },
    { id: "approved",      icon: CheckCircle,     label: "Approved" },
    { id: "production",    icon: Package,         label: "In production" },
    { id: "shipped",       icon: Truck,           label: "Shipped" },
    { id: "treatment",     icon: Heart,           label: "In treatment" },
    { id: "completed",     icon: CheckCircle,     label: "Completed" },
];

const NAV_PLANNER = [
    { to: "/coe", icon: ChartBar, label: "Dashboard", end: true },
    { to: "/coe/planner/queue", icon: Tray, label: "My Queue" },
    { to: "/coe/planner/provider-chat", icon: ChatCircleDots, label: "Provider Chat", badgeKey: "lab_chat" },
    { to: "/coe/profile", icon: UserIcon, label: "My Profile" },
];

const NAV_PRODUCTION = [
    { to: "/coe/production", icon: ChartBar, label: "Dashboard", end: true },
    { to: "/coe/production/queue", icon: Package, label: "Production queue" },
    { to: "/coe/production/my-stats", icon: ChartLineUp, label: "My Stats" },
    { to: "/coe/production/notifications", icon: Bell, label: "Notifications", badgeKey: "production_notifications" },
    { to: "/coe/compliance", icon: Heart, label: "Compliance" },
    { to: "/coe/profile", icon: UserIcon, label: "My Profile" },
];

/* iter97.88 — Scan technician portal (Phase A3). Mirrors the planner
   navigation shape so the user has a familiar shell: Dashboard, queue,
   personal profile. The Profile tab uses the same StaffProfile page
   every other COE staff uses; the technician can upload an avatar
   there. */
const NAV_TECHNICIAN = [
    { to: "/coe/technician", icon: ChartBar, label: "Dashboard", end: true },
    { to: "/coe/technician/visits", icon: Tray, label: "My Visits" },
    { to: "/coe/compliance", icon: Heart, label: "Compliance" },
    { to: "/coe/profile", icon: UserIcon, label: "My Profile" },
];

const NAV_SALES = [
    { to: "/coe", icon: ChartBar, label: "Dashboard", end: true },
    { to: "/coe/sales/b2b-leads", icon: Handshake, label: "B2B Leads" },
    { to: "/coe/sales", icon: Briefcase, label: "My clinics" },
    { to: "/coe/compliance", icon: Heart, label: "Compliance" },
    // iter101.40 — Sidebar parity with Planner / PES / Production etc.
    { to: "/coe/profile", icon: UserIcon, label: "My Profile" },
];

const NAV_SUPPORT = [
    { to: "/coe", icon: ChartBar, label: "Dashboard", end: true },
    // iter98.99ea — Support also picks up unclaimed leads (backend
    // allows counsellor / coe_lead / support).
    { to: "/coe/inbox", icon: Tray, label: "Shared Inbox" },
    { to: "/coe/support", icon: Lifebuoy, label: "Tickets" },
    { to: "/coe/compliance", icon: Heart, label: "Compliance" },
    // iter101.40 — Sidebar parity with Planner / PES / Production etc.
    { to: "/coe/profile", icon: UserIcon, label: "My Profile" },
];

const NAV_QA = [
    { to: "/coe/qa", icon: ChartBar, label: "Dashboard", end: true, subnav: QA_SUBNAV, subnavBase: "/coe/qa" },
    { to: "/coe/compliance", icon: Heart, label: "Compliance" },
];

// iter98.99dz — Every internal team member's portal now shows their role
// prefixed with "CoE-" so doctors/patients always understand who they're
// dealing with. Short forms here are sized for the sidebar (renders under
// the name); the full forms (see StaffProfile.jsx + permissions.py) are
// used in admin tables, profile pages, and the COE Overview cards.
// iter101.29 — Dropped the "CoE-" prefix per user direction. When a
// planner (or any staff member) sees their own role in the UI, they
// already know they're inside the COE portal — the prefix was noise.
// Full labels used in admin tables, profile pages, sidebars.
// Backend `role_label` returns are aligned too (see coe_extras.py).
const TYPE_LABELS = {
    counsellor: "Patient Engagement Specialist",
    technician: "Scanning Tech",
    internal_dentist: "Internal Dentist",
    coe_lead: "Operations Head",
    planner: "Treatment Planner",
    production: "Production Tech",
    sales: "Sales & KAM",
    support: "Triage & Support",
    qa: "Quality Assurance",
};

const NAV_BY_TYPE = {
    planner: NAV_PLANNER,
    production: NAV_PRODUCTION,
    technician: NAV_TECHNICIAN,
    sales: NAV_SALES,
    support: NAV_SUPPORT,
    qa: NAV_QA,
};

export default function CoeLayout() {
    const { user, loading, logout } = useAuth();
    const navigate = useNavigate();
    const [unread, setUnread] = useState({ lab_chat: 0, patient_chat: 0 });

    /* iter101.58 — Sidebar collapse toggle (mirrors admin + doctor
       portals). Default expanded per universal product direction;
       user's preference lives in sessionStorage so it resets on next
       browser session. */
    const [collapsed, setCollapsed] = useState(() => {
        try { localStorage.removeItem(COE_COLLAPSED_KEY); } catch { /* noop */ }
        const stored = sessionStorage.getItem(COE_COLLAPSED_KEY);
        if (stored === "1") return true;
        if (stored === "0") return false;
        return false;
    });
    useEffect(() => {
        sessionStorage.setItem(COE_COLLAPSED_KEY, collapsed ? "1" : "0");
    }, [collapsed]);

    // Poll chat-unread every 60s while the layout is mounted so the
    // Provider Chat sidebar badge stays fresh without dragging the page.
    const refreshUnread = useCallback(() => {
        api.get("/me/chat-unread").then((r) => setUnread(r.data || {})).catch(() => {});
    }, []);
    useEffect(() => {
        if (!user) return undefined;
        refreshUnread();
        const id = setInterval(refreshUnread, 60_000);
        return () => clearInterval(id);
    }, [user, refreshUnread]);

    if (loading) return <div className="p-12 text-center text-muted-foreground">Loading...</div>;
    if (!user) {
        // 2026-02-28 — logout-flow uniform "→ /". See AdminLayout for the
        // session-flag rationale; same pattern, different login fallback.
        const isLoggingOut = sessionStorage.getItem("isLoggingOut") === "1";
        if (isLoggingOut) sessionStorage.removeItem("isLoggingOut");
        // iter98.99dh — Always land on the portal's own login screen.
        return <Navigate to="/coe-login" replace />;
    }
    if (user.role !== "staff") return <Navigate to="/" replace />;

    // iter100.113 — Certification Authors have a dedicated /creator portal
    // (course-focused nav, not lead-focused). Bounce them there.
    if (user.staff_type === "course_creator") return <Navigate to="/creator" replace />;

    const NAV = NAV_BY_TYPE[user.staff_type] || NAV_LEADS;

    return (
        <div className="min-h-screen bg-secondary/30" data-testid="coe-layout">
            <header className="bg-card border-b border-border sticky top-0 z-50">
                <div className={`mx-auto px-5 sm:px-8 flex items-center justify-between h-16 ${collapsed ? "max-w-[1600px]" : "max-w-7xl"}`}>
                    <div className="flex items-center gap-4">
                        <InvisiqueLogo size="sm" />
                        <div className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] tracking-[0.2em] uppercase font-semibold">
                            <Headset size={10} weight="fill" /> COE
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-medium">{user.name}</div>
                            <div className="text-xs text-muted-foreground">{TYPE_LABELS[user.staff_type] || "Team member"}</div>
                        </div>
                        {/* iter97.14 — Production tech notification bell.
                            Surfaces new assignments, rework returns,
                            flag-acked unblocks, and SLA breaches. */}
                        {user.staff_type === "production" && <ProductionNotificationBell />}
                        {/* Universal notification bell for planners. Surfaces
                            new case assignments, doctor-requested plan
                            revisions, and refinement requests — same UX
                            pattern as the doctor's bell. */}
                        {user.staff_type === "planner" && <PlannerNotificationBell />}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { logout(); navigate("/coe-login"); }}
                            data-testid="coe-logout-btn"
                        >
                            <SignOut size={14} className="mr-1" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <div className={`mx-auto px-5 sm:px-8 py-8 flex ${collapsed ? "gap-3 lg:gap-4" : "gap-6 lg:gap-8"} ${collapsed ? "max-w-[1600px]" : "max-w-7xl"}`}>
                <aside
                    className={`shrink-0 transition-[width] duration-200 ${collapsed ? "w-16" : "w-72"}`}
                    data-testid="coe-sidebar"
                    data-collapsed={collapsed ? "true" : "false"}
                >
                    <div
                        className={`bg-card border border-border rounded-2xl sticky top-6 lg:top-20 flex flex-col ${collapsed ? "p-2" : "p-3"}`}
                        style={{ maxHeight: "calc(100vh - 3rem)" }}
                    >
                        <ProfileCard
                            user={user}
                            typeLabel={TYPE_LABELS[user.staff_type] || "Team member"}
                            collapsed={collapsed}
                        />
                        <nav className={`space-y-1 mt-3 flex-1 overflow-y-auto ${collapsed ? "" : "pr-1"}`}>
                            {NAV.map((n) => (
                                <SidebarItem key={n.to} item={n} unread={unread} collapsed={collapsed} />
                            ))}
                        </nav>

                        {/* iter101.58 — Universal footer collapse toggle
                            (matches AdminLayout + PortalLayout). Teal
                            circular icon button; glyph rotates 180° when
                            collapsed. */}
                        <button
                            type="button"
                            onClick={() => setCollapsed((v) => !v)}
                            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                            aria-pressed={collapsed}
                            className={`mt-3 self-center flex items-center justify-center w-8 h-8 rounded-full border border-primary/25 text-primary bg-primary/5 hover:bg-primary/15 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors ${collapsed ? "rotate-180" : ""}`}
                            data-testid="coe-sidebar-collapse-toggle"
                        >
                            <SidebarSimple size={16} weight="duotone" />
                        </button>
                    </div>
                </aside>
                <main className="flex-1 min-w-0">
                    <ErrorBoundary routeLabel="this page">
                        {/* iter101.87 — Expose sidebar collapsed state
                            to nested CoE pages so wide-form pages
                            (Planner Case Detail, Production Dashboard)
                            can widen on collapse via
                            `useOutletContext`. */}
                        <Outlet context={{ sidebarCollapsed: collapsed }} />
                    </ErrorBoundary>
                </main>
            </div>
        </div>
    );
}


/* Compact identity card at the top of the COE sidebar — mirrors the way the
   doctor portal surfaces who you are. Shows photo (or initials fallback) plus
   role; phone numbers are intentionally hidden everywhere in the chrome.
   iter101.58 — Collapses to an avatar-only pill when the sidebar is collapsed. */
function ProfileCard({ user, typeLabel, collapsed = false }) {
    const name = user?.name || "";
    const photo = user?.image || "";
    const initials = (name || "?")
        .split(/\s+/).filter(Boolean).slice(0, 2)
        .map((w) => w[0]?.toUpperCase()).join("") || "?";
    return (
        <NavLink
            to="/coe/profile"
            className={`block border-b border-border/60 hover:bg-secondary/40 transition-colors group ${collapsed ? "flex justify-center py-2" : "px-1 py-2"}`}
            data-testid="coe-sidebar-profile-card"
            title={collapsed ? `${name} · ${typeLabel}` : undefined}
        >
            <div className={`flex items-center ${collapsed ? "" : "gap-3"}`}>
                <div
                    className={`rounded-full overflow-hidden grid place-items-center shrink-0 ring-2 ring-primary/10 group-hover:ring-primary/30 transition ${collapsed ? "w-9 h-9" : "w-11 h-11"}`}
                    style={photo ? {} : { background: "linear-gradient(135deg, hsl(280 60% 55%), hsl(200 70% 50%))" }}
                    data-testid="coe-sidebar-profile-avatar"
                >
                    {photo ? (
                        <img src={photo} alt={name} className="w-full h-full object-cover" />
                    ) : (
                        <span className="text-white text-sm font-display font-bold">{initials}</span>
                    )}
                </div>
                {!collapsed && (
                    <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate" data-testid="coe-sidebar-profile-name">{name}</div>
                        {/* iter101.30 — Auto-shrink the role label when the
                            text runs long (Patient Engagement Specialist is
                            29 chars, breaks the sidebar rail). Below 18 chars
                            we use the standard 10px/0.16em spec; longer labels
                            drop to 9px + tighter tracking + full label with
                            title tooltip fallback so admins still see it in
                            full on hover. */}
                        {(() => {
                            const long = (typeLabel || "").length > 18;
                            return (
                                <div
                                    className={`uppercase text-muted-foreground truncate ${long ? "text-[9px] tracking-[0.08em]" : "text-[10px] tracking-[0.16em]"}`}
                                    title={typeLabel}
                                    data-testid="coe-sidebar-profile-role"
                                >
                                    {typeLabel}
                                </div>
                            );
                        })()}
                    </div>
                )}
            </div>
        </NavLink>
    );
}

function SidebarItem({ item, unread = {}, collapsed = false }) {
    const badgeCount = item.badgeKey ? Number(unread?.[item.badgeKey] || 0) : 0;
    return (
        <>
            <NavLink
                to={item.to}
                end={item.end}
                title={collapsed ? item.label : undefined}
                data-testid={`coe-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                    `flex items-center rounded-xl text-sm transition-colors ${
                        collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-3 py-2.5"
                    } ${
                        isActive ? "bg-primary text-white" : "text-foreground hover:bg-secondary"
                    }`
                }
            >
                <item.icon size={18} weight="duotone" />
                {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                {!collapsed && badgeCount > 0 && (
                    <span
                        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold tabular-nums shrink-0 group-[.bg-primary]:bg-white group-[.bg-primary]:text-primary"
                        data-testid={`coe-nav-badge-${item.badgeKey}`}
                    >
                        {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                )}
                {collapsed && badgeCount > 0 && (
                    <span
                        className="absolute mt-[-14px] ml-4 inline-flex items-center justify-center min-w-[14px] h-[14px] px-1 rounded-full bg-primary text-white text-[9px] font-bold tabular-nums ring-2 ring-card"
                        data-testid={`coe-nav-badge-${item.badgeKey}`}
                    >
                        {badgeCount > 9 ? "9+" : badgeCount}
                    </span>
                )}
            </NavLink>
            {item.subnav && !collapsed && (
                <div className="mt-1 mb-2 pl-3 ml-3 border-l border-border space-y-0.5">
                    {item.subnav.map((s) => (
                        <SubNavLink key={s.id} item={s} basePath={item.subnavBase} />
                    ))}
                </div>
            )}
        </>
    );
}

/**
 * Sub-nav links use ?bucket=<id> to drive the page filter. We compute active
 * state ourselves (NavLink can't read query strings).
 */
function SubNavLink({ item, basePath }) {
    const active = (() => {
        if (typeof window === "undefined") return false;
        if (window.location.pathname !== basePath) return false;
        const params = new URLSearchParams(window.location.search);
        const current = params.get("bucket") || "all";
        return current === item.id;
    })();
    return (
        <NavLink
            to={`${basePath}?bucket=${item.id}`}
            data-testid={`coe-subnav-${item.id}`}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-colors ${
                active ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
        >
            <item.icon size={13} weight={active ? "fill" : "duotone"} />
            <span className="truncate">{item.label}</span>
        </NavLink>
    );
}
